import React, { useEffect, useState } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import useTabMusic from "@/hooks/useTabMusic";
import { Skull, Heart, Sword, Shield, Eye, CheckCircle, XCircle, Crown, RotateCcw, Loader2, Coins, Sparkles, Users, Gem } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { t } from "@/utils/i18n";
import { resolveImageUrl } from "@/utils/imageUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Bestiary({ onReplayStarted }) {
  const { game, loadGame } = useGame();
  const { token, API } = useAuth();
  const [replayingName, setReplayingName] = useState(null);
  // Iter 100 — live drop-rates so the bestiary cards reflect the
  // admin-managed config (chances change on save, no game restart).
  const [dropRates, setDropRates] = useState(null);
  useTabMusic('bestiary');

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/config/drop-rates`)
      .then((r) => { if (alive) setDropRates(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [API]);

  if (!game) return null;

  const lang = game.language || "en";
  const bestiary = game.bestiary || [];
  const defeated = bestiary.filter(b => b.defeated).length;
  // Battle is "in progress" only if the state is meaningfully active.
  // A bare/empty/null battle_state should NOT block replays — backend will
  // auto-clean stale states on replay.
  const bs = game.battle_state;
  const hasBattle = !!(bs && (bs.active === true || bs.pending === true));

  const handleReplay = async (enemyName) => {
    if (!game?.id || replayingName) return;
    if (hasBattle) {
      toast.error(lang.startsWith("it") ? "Termina la battaglia in corso" : "Finish the current battle first");
      return;
    }
    setReplayingName(enemyName);
    try {
      await axios.post(
        `${API}/game/${game.id}/battle/replay`,
        { enemy_name: enemyName },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
      toast.success(lang.startsWith("it") ? `Rigioca: ${enemyName}` : `Replay: ${enemyName}`);
      await loadGame(game.id);
      if (onReplayStarted) onReplayStarted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replay failed");
    } finally {
      setReplayingName(null);
    }
  };

  return (
    <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="bestiary-view">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="font-heading text-base sm:text-xl text-[#cfb53b] flex items-center gap-2">
            <Skull size={18} /> {t(lang, 'bestiary_title')}
          </h2>
          <span className="text-xs sm:text-sm text-[#94a3b8] font-heading">
            {defeated}/{bestiary.length} {t(lang, 'bestiary_defeated')}
          </span>
        </div>

        {bestiary.length === 0 ? (
          <div className="text-center py-12 sm:py-16 border border-dashed border-[#8b0000]/20 rounded-lg">
            <Skull size={48} className="mx-auto text-[#8b0000]/20 mb-3 sm:mb-4" />
            <p className="text-[#94a3b8] font-body text-sm sm:text-base">{t(lang, 'bestiary_empty')}</p>
            <p className="text-[#64748b] text-xs sm:text-sm mt-1">{t(lang, 'bestiary_hint')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 stagger-children">
            {bestiary.map((creature, idx) => (
              <div
                key={idx}
                data-testid={`bestiary-entry-${idx}`}
                className={`bg-[#1a1a1d]/80 border rounded-lg overflow-hidden transition-all animate-fadeInUp group hover:border-[#8b0000]/40 ${
                  creature.is_boss ? 'border-[#cfb53b]/40 ring-1 ring-[#cfb53b]/20' : creature.defeated ? 'border-[#8b0000]/25' : 'border-[#2d3748]'
                }`}
              >
                <div className="flex gap-3 sm:gap-4 p-3 sm:p-4">
                  {/* Enemy Image or Skull icon */}
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border shrink-0 bg-[#0a0a0c] flex items-center justify-center ${creature.is_boss ? 'border-[#cfb53b]/40' : 'border-[#8b0000]/20'}`}>
                    {creature.image_url ? (
                      <img
                        src={resolveImageUrl(creature.image_url, BACKEND_URL)}
                        alt={creature.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className={`items-center justify-center ${creature.image_url ? 'hidden' : 'flex'}`}>
                      {creature.is_boss ? <Crown size={28} className="text-[#cfb53b]/40" /> : <Skull size={28} className="text-[#8b0000]/40" />}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {creature.is_boss && <Crown size={12} className="text-[#cfb53b] shrink-0" />}
                        <h3 className={`font-heading text-sm sm:text-base truncate group-hover:text-[#cfb53b] transition-colors ${creature.is_boss ? 'text-[#cfb53b]' : 'text-[#e2e8f0]'}`}>
                          {creature.name}
                        </h3>
                        {creature.is_boss && <span className="text-[8px] font-heading bg-[#cfb53b]/20 text-[#cfb53b] px-1 py-0.5 rounded shrink-0">{t(lang, 'boss_label')}</span>}
                      </div>
                      {creature.defeated ? (
                        <CheckCircle size={14} className="text-[#10b981] shrink-0" />
                      ) : (
                        <XCircle size={14} className="text-[#8b0000] shrink-0" />
                      )}
                    </div>

                    {creature.description && (
                      <p className="text-[10px] sm:text-xs text-[#94a3b8] line-clamp-2 mb-2 leading-relaxed">{creature.description}</p>
                    )}

                    {/* Stats */}
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
                      <span className="flex items-center gap-1 text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded">
                        <Heart size={9} /> {creature.max_hp}
                      </span>
                      <span className="flex items-center gap-1 text-[#cfb53b] bg-[#cfb53b]/10 px-1.5 py-0.5 rounded">
                        <Sword size={9} /> {creature.attack}
                      </span>
                      <span className="flex items-center gap-1 text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded">
                        <Shield size={9} /> {creature.defense}
                      </span>
                      <span className="flex items-center gap-1 text-[#94a3b8] bg-[#2d3748] px-1.5 py-0.5 rounded">
                        <Eye size={9} /> x{creature.times_fought}
                      </span>
                    </div>

                    {/* Loot table preview (shown only when defeated) */}
                    {creature.defeated && creature.loot_table && (() => {
                      // Iter 100 — show LIVE drop rates from admin config.
                      // Fall back to the bestiary loot_table snapshot if
                      // /config/drop-rates hasn't loaded yet.
                      const isBoss = !!creature.is_boss;
                      const allyChance = dropRates
                        ? (isBoss
                            ? dropRates.ally?.boss_chance ?? 0.05
                            : dropRates.ally?.normal_chance ?? 0.15)
                        : (creature.loot_table.ally_card_chance ?? 0);
                      const foilChance = dropRates?.foil?.battle_drop ?? 0.02;
                      const dustMult = dropRates?.dust?.global_multiplier ?? 1.0;
                      const dustMin = Math.round((creature.loot_table.dust?.[0] ?? 0) * dustMult);
                      const dustMax = Math.round((creature.loot_table.dust?.[1] ?? 0) * dustMult);
                      return (
                        <div className="mt-2 pt-2 border-t border-[#2d3748]/60">
                          <p className="text-[9px] sm:text-[10px] text-[#94a3b8]/80 font-heading uppercase tracking-wider mb-1">
                            {lang.startsWith("it") ? "Bottino al replay" : "Replay loot"}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
                            <span className="flex items-center gap-1 text-[#fbbf24]" title="Oro">
                              <Coins size={9} />
                              {creature.loot_table.gold?.[0] ?? 0}–{creature.loot_table.gold?.[1] ?? 0}
                            </span>
                            <span className="flex items-center gap-1 text-[#a78bfa]" title={`Polvere arcana (mult. ${dustMult.toFixed(1)}×)`}>
                              <Sparkles size={9} />
                              {dustMin}–{dustMax}
                            </span>
                            <span
                              className="flex items-center gap-1 text-[#34d399]"
                              title={lang.startsWith("it") ? "Chance carta alleato" : "Ally card chance"}
                            >
                              <Users size={9} />
                              {Math.round(allyChance * 100)}%
                            </span>
                            {foilChance > 0 && (
                              <span
                                className="flex items-center gap-1 text-purple-300"
                                title={lang.startsWith("it") ? "Chance versione foil sul drop" : "Foil chance on drop"}
                              >
                                <Gem size={9} />
                                {(foilChance * 100).toFixed(1)}%
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Status bar */}
                <div className={`px-3 sm:px-4 py-1.5 text-[10px] font-heading flex items-center justify-between gap-2 ${
                  creature.defeated ? 'bg-[#10b981]/10 text-[#10b981]' : 'bg-[#8b0000]/10 text-[#8b0000]'
                }`}>
                  <span className="truncate">
                    {creature.defeated ? t(lang, 'bestiary_slain') : t(lang, 'bestiary_survived')}
                    {creature.times_fought > 1 && ` - ${t(lang, 'bestiary_fought')} ${creature.times_fought}x`}
                  </span>
                  {creature.defeated && (
                    <Button
                      data-testid={`replay-${idx}`}
                      size="sm"
                      onClick={() => handleReplay(creature.name)}
                      disabled={replayingName !== null || hasBattle}
                      className={`h-7 px-3 text-[10px] font-heading shrink-0 border transition-all ${
                        creature.is_boss
                          ? "bg-[#fbbf24] hover:bg-[#fcd34d] text-[#0a0a0c] border-[#fbbf24]"
                          : "bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] border-[#cfb53b]"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={lang.startsWith("it") ? "Rigioca (ricompense dimezzate)" : "Replay (halved rewards)"}
                    >
                      {replayingName === creature.name ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : (
                        <>
                          <RotateCcw size={11} className="mr-1" />
                          {lang.startsWith("it") ? "Rigioca" : "Replay"}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
